// QuestionSeleccionMultiple - Pregunta tipo Selección Múltiple (varias opciones)
// Sprint 3 - Mobile-first design

import React, { useState } from 'react';
import {
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Chip,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { PreguntaCompleta } from '../../../../types/formulario.types';
import { QuestionResponse } from '../../../../types/audit.types';

interface QuestionSeleccionMultipleProps {
  pregunta: PreguntaCompleta;
  respuesta?: QuestionResponse;
  onChange: (valor: any, comentario?: string) => void;
}

const QuestionSeleccionMultiple: React.FC<QuestionSeleccionMultipleProps> = ({
  pregunta,
  respuesta,
  onChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const valorActual = (respuesta as any)?.response_value as { opciones?: string[] } | undefined;
  const [comentario, setComentario] = useState((respuesta as any)?.comment || '');
  const opcionesSeleccionadas = valorActual?.opciones || [];

  const handleChange = (opcion: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const nuevasOpciones = event.target.checked
      ? [...opcionesSeleccionadas, opcion]
      : opcionesSeleccionadas.filter((o) => o !== opcion);
    
    onChange({ opciones: nuevasOpciones }, comentario || undefined);
  };

  const handleChipClick = (opcion: string) => () => {
    const isSelected = opcionesSeleccionadas.includes(opcion);
    const nuevasOpciones = isSelected
      ? opcionesSeleccionadas.filter((o) => o !== opcion)
      : [...opcionesSeleccionadas, opcion];
    
    onChange({ opciones: nuevasOpciones }, comentario || undefined);
  };

  const handleComentarioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nuevoComentario = event.target.value;
    setComentario(nuevoComentario);
    onChange({ opciones: opcionesSeleccionadas }, nuevoComentario || undefined);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box sx={{ fontSize: { xs: '1rem', sm: '1.0625rem' }, fontWeight: 600 }}>
            {(pregunta as any).text}
          </Box>
          {(pregunta as any).required && (
            <Chip label="Obligatoria" size="small" color="error" sx={{ height: 20, fontSize: '0.6875rem' }} />
          )}
        </Box>
        {(pregunta as any).description && (
          <Box sx={{ fontSize: '0.875rem', color: 'text.secondary', fontStyle: 'italic', mt: 0.5 }}>
            {(pregunta as any).description}
          </Box>
        )}
      </Box>

      {/* Sección de respuesta */}
      <Box 
        sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          bgcolor: 'action.hover', 
          borderRadius: 1,
          mb: pregunta.permite_comentario ? 2 : 0,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
            }}
          >
            Selecciona todas las que apliquen
          </Typography>
          {opcionesSeleccionadas.length > 0 && (
            <Chip
              label={`${opcionesSeleccionadas.length} seleccionada${opcionesSeleccionadas.length !== 1 ? 's' : ''}`}
              size="small"
              color="primary"
              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
            />
          )}
        </Box>

        {/* Mobile: Chips grandes touch-friendly */}
        {isMobile && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(pregunta as any).options?.map((opcion: any, index: number) => {
              const opcionLabel = opcion.label || opcion.texto || opcion.value;
              const isSelected = opcionesSeleccionadas.includes(opcionLabel);
              
              return (
                <Chip
                  key={`${pregunta.id}-chip-${index}`}
                  label={opcionLabel}
                  icon={isSelected ? <CheckIcon /> : undefined}
                  onClick={handleChipClick(opcionLabel)}
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  sx={{
                    height: 42,
                    fontSize: '0.9375rem',
                    fontWeight: isSelected ? 600 : 500,
                    px: 1.5,
                    borderWidth: 2,
                    transition: 'all 0.2s ease',
                    '&:active': {
                      transform: 'scale(0.95)',
                    },
                    ...(isSelected && {
                      boxShadow: 1,
                    }),
                  }}
                />
              );
            })}
          </Box>
        )}

        {/* Desktop: Checkboxes tradicionales */}
        {!isMobile && (
          <FormGroup>
            {(pregunta as any).options?.map((opcion: any, index: number) => {
              const opcionLabel = opcion.label || opcion.texto || opcion.value;
              return (
                <FormControlLabel
                  key={`${pregunta.id}-opcion-${index}`}
                  control={
                    <Checkbox
                      checked={opcionesSeleccionadas.includes(opcionLabel)}
                      onChange={handleChange(opcionLabel)}
                    />
                  }
                  label={opcionLabel}
                  sx={{
                    mb: 0.5,
                    '& .MuiFormControlLabel-label': {
                      fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                    },
                  }}
                />
              );
            })}
          </FormGroup>
        )}
      </Box>

      {/* Comentario */}
      {pregunta.permite_comentario && (
        <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block', 
              mb: 1, 
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Comentario Adicional
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Comentario (opcional)"
            value={comentario}
            onChange={handleComentarioChange}
            placeholder="Agrega un comentario..."
            variant="outlined"
            size="small"
            name={`comentario-${pregunta.id}`}
            id={`comentario-seleccion-${pregunta.id}`}
            inputProps={{
              name: `comentario-${pregunta.id}`,
            }}
            InputProps={{
              name: `comentario-${pregunta.id}`,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default QuestionSeleccionMultiple;
